import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  useParams,
} from 'react-router-dom';
import clsx from 'clsx';
import {
  useNavigateToBack,
  useNotify,
} from 'core/hooks';
import isNil from 'lodash/isNil';
import Spinner from 'pages/Dashboard/components/Spinner';
import PatientDetailsCard from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails';
import VisitNoteFormOutlet, {
  FormCommands,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteFormOutlet';
import VisitNoteHeader from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteHeader';
import {
  useGetVisitNote,
  useSaveVisitNote,
  useUnfinalizeVisitNote,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  BillingState,
  VisitNote as VisitNoteType,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import {
  postApiVisitNotePrintVisitNotePdfEncounterId,
} from 'endpoints';
import {
  usePrintMediaRequest,
} from 'views/EMR/hooks/media';
import PanelExtension from 'core/components/PanelExtension';
import LoadError from 'core/components/LoadError';
import ViewProvider, {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import {
  EncounterViewModel,
  VisitNoteDTO,
} from 'dtos';
import {
  useSelectedEntitiesActions,
} from 'views/EMR/stores/entities';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import useVisitNotePCCFinalize from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/useVisitNotePCCFinalize';
import VisitNoteFormBridgeProvider from 'pages/Dashboard/pages/Encounters/pages/VisitNote/contexts/visitNoteFormBridge';
import {
  AxiosError,
} from 'axios';
import {
  getErrorMessage,
} from 'utils/errors';
import useSignalREffect from 'views/EMR/hooks/signalR';

function VisitNoteOutlet() {
  const { setExtensionExpanded, extensionExpanded, Extension } = useContext(ViewContext);
  const { id = '' } = useParams<{ id: string }>();
  const {
    data: visitNote,
    isLoading: loading,
    refetch,
    isFetching,
    isError,
    error: visitNoteError,
  } = useGetVisitNote(Number(id));
  const { mutateAsync: saveVisitNote, isPending: savingVisitNote } = useSaveVisitNote();
  const {
    mutateAsync: unfinalizeVisitNote,
    isPending: unfinalizingVisitNote,
  } = useUnfinalizeVisitNote();
  const notify = useNotify();
  const [formCommands, setFormCommands] = useState<FormCommands>({
    applyMasterTemplate: () => {},
    openBulkSectionTemplates: () => {},
    prepareForExternalUpdate: async () => false,
  });

  const billingState = visitNote?.bill?.state;
  const nextState = billingState === BillingState.Review
    ? BillingState.ReviewCompleted
    : BillingState.Review;

  const { print: printVisitNote, isPrinting } = usePrintMediaRequest<
    [number]
  >(postApiVisitNotePrintVisitNotePdfEncounterId);

  const errorMessage = getErrorMessage(visitNoteError);

  const onSubmit = async (formData: VisitNoteData) => {
    try {
      if (!savingVisitNote) {
        return await saveVisitNote(formData);
      }
      return null;
    } catch (error) {
      console.error('Visit note save error=', error, 'status=', (error as AxiosError).response?.status);
      if ((error as AxiosError).response?.status === 409) {
        visitNoteEventChannel.emit('onVisitNoteConflict', true);
      }
      throw error;
    }
  };

  const { handleFinalize } = useVisitNotePCCFinalize({
    visitNoteId: visitNote?.visitNoteId,
    patientId: visitNote?.patientId,
    encounterId: visitNote?.encounterId,
    onSubmit,
    refetch: async () => {
      const result = await refetch();
      return result.data as VisitNoteDTO;
    },
  });

  useSignalREffect(
    'OnFinalizedVisitNotePdfReady',
    ({ encounterId }) => {
      if (encounterId === visitNote?.encounterId) {
        refetch();
      }
    },
    [refetch, visitNote?.encounterId],
  );

  useSignalREffect(
    'OnVisitNoteUpdated',
    ({ encounterId }) => {
      if (encounterId === visitNote?.encounterId) {
        refetch();
      }
    },
    [refetch, visitNote?.encounterId],
  );

  const onSave = async (formData: VisitNoteData, shouldNotify = true) => {
    try {
      const saved = await onSubmit(formData);
      await refetch();
      if (shouldNotify) {
        notify({
          variant: 'success',
          message: 'Visit Note saved successfully!',
        });
      }
      return saved;
    } catch (error) {
      console.error('onSave error', error, (error as AxiosError).response?.status);
      throw error;
    }
  };

  const onSilentSave = async (formData: VisitNoteData) => onSave(formData, false);

  const onPrint = async (formData: VisitNoteData) => {
    try {
      if (!visitNote?.isFinalized) {
        const saved = await onSilentSave(formData);
        if (isNil(saved)) {
          return;
        }
      }

      await printVisitNote([visitNote!.encounterId]);
    } catch (error) {
      console.error('Visit note print error=', error);
    }
  };

  const onUnfinalize = async (formData: VisitNoteData) => {
    try {
      await onSave(formData);
      const updatedData = await unfinalizeVisitNote(visitNote!.visitNoteId);
      await refetch();
      visitNoteEventChannel.emit('onVisitNoteFinalized');
      return updatedData;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const updateBillingState = async (formData: VisitNoteData) => {
    try {
      const updatedData = await onSubmit({
        ...visitNote,
        ...formData,
        bill: {
          ...(formData?.bill ?? {}),
          state: nextState ?? BillingState.Review,
        },
      });
      refetch();
      return updatedData;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const { setEntity, unsetEntity } = useSelectedEntitiesActions();

  useEffect(() => {
    if (!isNil(visitNote?.patient)) {
      setEntity(visitNote?.patient!, visitNote?.patient?.patientId!);
    }
    return () => {
      unsetEntity(visitNote?.patient?.patientId ?? 0);
    };
  }, [visitNote?.patient, setEntity, unsetEntity]);

  const navigateToBack = useNavigateToBack();

  const onClose = () => {
    navigateToBack();
  };

  const handleRefreshVisitNote = useCallback(async () => {
    await refetch();
    visitNoteEventChannel.emit('onVisitNoteConflict', false);
  }, [refetch]);

  return isError ? (
    <LoadError
      title="Unable to load visit note"
      message={errorMessage}
      isRetrying={isFetching}
      onRetry={() => {
        refetch();
      }}
      onGoBack={onClose}
    />
  ) : (
    <div className={clsx('relative flex min-w-0 flex-1 overflow-x-auto overflow-y-hidden nw-visit-note', {
      'justify-center items-center': loading,
      'justify-between': !loading,
    })}
    >
      {loading && <Spinner size={30} />}
      {!loading && !isNil(visitNote) && (
        <VisitNoteFormBridgeProvider>
          <>
            <div className="flex h-full min-w-[640px] flex-1 flex-col">
              <VisitNoteHeader
                patient={visitNote.patient!}
                encounterId={visitNote.encounterId}
                patientId={visitNote.patientId ?? 0}
                visitDateTime={visitNote.visitDateTime}
                visitNoteId={visitNote.visitNoteId}
                editable={!visitNote.isFinalized}
                savingVisitNote={savingVisitNote}
                onClose={onClose}
                onRefreshVisitNote={handleRefreshVisitNote}
                isRefreshingVisitNote={isFetching}
                onApplyMasterTemplate={(template) => {
                  formCommands.applyMasterTemplate(template);
                }}
                onOpenBulkSectionTemplates={formCommands.openBulkSectionTemplates}
                onBeforePCCEnroll={formCommands.prepareForExternalUpdate}
              />
              <VisitNoteFormOutlet
                visitNote={visitNote as VisitNoteType}
                editable={!visitNote.isFinalized}
                savingVisitNote={savingVisitNote}
                unfinalizingVisitNote={unfinalizingVisitNote}
                isPrinting={isPrinting}
                onCommandsChange={setFormCommands}
                onSave={async (data) => { await onSave(data); }}
                onSilentSave={onSilentSave}
                onFinalize={handleFinalize}
                onUnfinalize={onUnfinalize}
                onUpdateBillingState={updateBillingState}
                onPrint={visitNote.visitNoteId > 0 ? onPrint : undefined}
              />
            </div>
            <div className="relative flex h-full w-[25rem] min-w-[340px] shrink-0 flex-col gap-2 overflow-hidden rounded-r-xl bg-gray-50 pt-4 2xl:w-[32rem]">
              <PatientDetailsCard
                className="!p-0 !bg-transparent !rounded-none"
                encounter={visitNote as Partial<EncounterViewModel>}
                tabs={['info', 'pastNotes', 'imaging', 'report']}
              />
              <div className={clsx('transition-all duration-100 absolute inset-0 w-0', {
                'w-full': extensionExpanded,
              })}
              >
                <PanelExtension className="w-full rounded-r-xl">
                  {!isNil(Extension) && (
                    <Extension
                      patient={visitNote.patient!}
                      className="bg-gray-100"
                      classes={{ list: 'px-4 ' }}
                      onClose={() => { setExtensionExpanded?.(null); }}
                    />
                  )}
                </PanelExtension>
              </div>
            </div>
          </>
        </VisitNoteFormBridgeProvider>
      )}
    </div>
  );
}

export default function VisitNote() {
  return (
    <ViewProvider>
      <VisitNoteOutlet />
    </ViewProvider>
  );
}
